import { useState, useEffect, useCallback } from "react";
import { useMediaQuery } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useApi } from "../utils/Api";
import Big from "../components/booking/Big";
import SpeedMenu from "../components/SpeedMenu";
import SchoolIcon from "@mui/icons-material/School";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CourseGradesDialogBase from "../components/courses/grades/CourseGradesDialogBase";
import Translate from "../utils/Translate";
import Preloader from "../components/Preloader";
import { hasAccess, hasSomeAccess, accessKeys } from "../utils/userAccess";
import { getLicenseRegistrationCourseTypes } from "../utils/externalUserHelper";
import BookingFlowDialogContainer from "../components/courses/bookings/BookingFlowDialogContainer";
import YesOrNoDialog from "../components/YesOrNoDialog";
import ActorPermitFlowDialogContainer from "../components/actor-permits/ActorPermitFlowDialogContainer";
import { PersonAddAlt1 } from "@mui/icons-material";
import ID06CompetencesDialogBase from "../components/id06/ID06CompetencesDialogBase";
import SkillnationDialogBase from "../components/skillnation/SkillnationDialogBase";
import { hasIntegration, integrations } from "../utils/integrations";
import ActorPermitFlowDialogHeader from "../components/actor-permits/ActorPermitFlowDialogHeader";
import { maxPermitsBatchSize } from "../utils/actorPermit";

//import SpeedMenu from '../components/SpeedMenu'

async function canCreateActorPermitForCourse(api, courseId) {
  try {
    var permitSkillGroups = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}skillgroups/for-permits?includeImageUrls=false`,
      false,
      "GET",
    );
    var courseSkillGroups = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}skillgroups/for-course?courseId=${courseId}&includeImageUrls=false`,
      false,
      "GET",
    );
    var permitSkillGroupIds = permitSkillGroups.map((sg) => sg.skillGroupId);
    return courseSkillGroups.some((sg) => permitSkillGroupIds.includes(sg.skillGroupId));
  } catch (error) {
    return false;
  }
}

const Bookings = (props) => {
  const bigScreen = useMediaQuery("(min-width:1200px)");
  const api = useApi();
  let courseId = useParams().id;
  if (props.id) {
    courseId = props.id;
  }
  const [isLoading, setIsLoading] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showCourseFullyBookedDialog, setShowCourseFullyBookedDialog] = useState(false);
  const [showID06Dialog, setShowID06Dialog] = useState(window.location.hash.startsWith("#id06"));
  const [showID06OpenQuestion, setShowID06OpenQuestion] = useState(false);
  const [showSkillnationDialog, setShowSkillnationDialog] = useState(window.location.hash.startsWith("#skillnation"));
  const [showSkillnationOpenQuestion, setShowSkillnationOpenQuestion] = useState(false);
  const [showDeleteCourseQuestion, setShowDeleteCourseQuestion] = useState(false);
  const [course, setCourse] = useState(false);
  const [stats, setStats] = useState(false);
  const [showGradesDialog, setShowGradesDialog] = useState(window.location.hash.startsWith("#grades"));
  const [licenseRegistrationCourseTypes, setLicenseRegistrationCourseTypes] = useState([]);
  const [isActorPermitCourse, setIsActorPermitCourse] = useState(false);
  const [hasActorPermit] = useState(hasAccess(accessKeys.actorPermit));

  const hasStandardOrAdvanced = hasSomeAccess(accessKeys.standardPackage, accessKeys.advancedPackage);
  const isSwedishCompany = hasAccess(accessKeys.swedishCompany);
  const navigate = useNavigate();

  const loadCourse = useCallback(
    async (ignoreLoadingSpinner = false) => {
      !ignoreLoadingSpinner && setIsLoading(true);
      const [statsData, courseData, courseTypes] = await Promise.all([
        api.fetch(`${import.meta.env.VITE_MAIN_URL}courses/${courseId}/stats`, false, "GET"),
        api.fetch(`${import.meta.env.VITE_MAIN_URL}courses/${courseId}/`, false, "GET"),
        getLicenseRegistrationCourseTypes(),
      ]);

      if (statsData && statsData.isSuccessful) {
        setStats(statsData);
      }

      if (courseData && courseData.isSuccessful) {
        window.global.bookings = {};
        setCourse(courseData);
      }

      setLicenseRegistrationCourseTypes(courseTypes);

      if (hasActorPermit) {
        const canCreatePermit = await canCreateActorPermitForCourse(api, courseId);
        setIsActorPermitCourse(!!canCreatePermit);
      }

      !ignoreLoadingSpinner && setIsLoading(false);
      return courseData && courseData.isSuccessful ? courseData : null;
    },
    [courseId, hasActorPermit, api],
  );

  useEffect(() => {
    loadCourse();
  }, [loadCourse]);

  function handleOpenBookingDialog() {
    setShowBookingDialog(true);
  }

  async function handleCloseBookingDialog() {
    setShowBookingDialog(false);
    const loadedCourse = await loadCourse();
    if (!!loadedCourse && loadedCourse.remainingPlaces <= 0 && !!loadedCourse.publishFromDate) {
      setShowCourseFullyBookedDialog(true);
    }
  }

  async function unpublishFromWeb() {
    await api.fetch(`${import.meta.env.VITE_MAIN_URL}courses/${courseId}/unpublish`);
    await loadCourse(true);
  }

  function handleOpenGradesDialog() {
    window.location.hash = "#grades";
    setShowGradesDialog(true);
  }

  function handleCloseGradesDialog(orderedDigitalLicenses) {
    window.history.back();
    setShowGradesDialog(false);
    loadCourse();

    // We only show a question if the userr doesn't have multiple integrations
    if (!(hasIntegration(integrations.ID06) && hasIntegration(integrations.SsgSkillnation))) {
      if (orderedDigitalLicenses && hasIntegration(integrations.ID06)) {
        setShowID06OpenQuestion(true);
      }
      if (orderedDigitalLicenses && hasIntegration(integrations.SsgSkillnation)) {
        setShowSkillnationOpenQuestion(true);
      }
    }
  }

  function handleOpenID06Dialog() {
    window.location.hash = "#id06";
    setShowID06OpenQuestion(false);
    setShowID06Dialog(true);
  }

  function handleCloseID06Dialog() {
    window.history.back();
    setShowID06Dialog(false);
  }

  function handleOpenSkillnationDialog() {
    window.location.hash = "#skillnation";
    setShowSkillnationOpenQuestion(false);
    setShowSkillnationDialog(true);
  }

  function handleCloseSkillnationDialog() {
    window.history.back();
    setShowSkillnationDialog(false);
  }

  const speedDialActions = [
    ...(isSwedishCompany && !hasStandardOrAdvanced
      ? [
          {
            hide: course?.numberOfParticipants < 1,
            icon: <ContactMailIcon />,
            text: Translate.get("OrderMultipleLicenses"),
            disabled: !course || !licenseRegistrationCourseTypes.includes(course.licenseGroupDescription),
            route: `/dashboard/licenseregistration?courseId=${courseId}`,
          },
        ]
      : []),
    ...(hasStandardOrAdvanced
      ? [
          {
            icon: <SchoolIcon />,
            text: Translate.get("GradeCourse"),
            disabled:
              course?.numberOfParticipants <= 0 ||
              !course.courseBookings?.some((x) => x?.courseParticipants?.some((y) => y?.courseParticipantId !== -1)),
            onClick: handleOpenGradesDialog,
          },
        ]
      : []),
    ...(hasIntegration(integrations.ID06)
      ? [
          {
            icon: <SchoolIcon />,
            text: Translate.get("ID06Name"),
            // Disabled until some student has received a digital license
            disabled:
              !course ||
              !course.courseBookings.some((cb) =>
                cb.courseParticipants.some((cp) => cp.actorLicense && cp.actorLicense.digitalLicenseId),
              ),
            onClick: handleOpenID06Dialog,
          },
        ]
      : []),
    ...(hasIntegration(integrations.SsgSkillnation)
      ? [
          {
            icon: <SchoolIcon />,
            text: Translate.get("SsgSkillnationName"),
            // Disabled until some student has received a digital license
            disabled:
              !course ||
              !course.courseBookings.some((cb) =>
                cb.courseParticipants.some((cp) => cp.actorLicense && cp.actorLicense.digitalLicenseId),
              ),
            onClick: handleOpenSkillnationDialog,
          },
        ]
      : []),
    ...(hasActorPermit
      ? [
          {
            icon: <ReceiptLongIcon />,
            text: Translate.get("CreateActorPermit"),
            disabled:
              !isActorPermitCourse ||
              !course?.courseBookings?.some((cb) => cb.courseParticipants.some((cp) => !!cp.actorLicense)) ||
              course?.courseBookings?.flatMap((cb) => cb.courseParticipants.filter((cp) => !!cp.actorLicense)).length >
                maxPermitsBatchSize,
            dialogHeader: <ActorPermitFlowDialogHeader />,
            component: (
              <ActorPermitFlowDialogContainer
                actorIds={course?.courseBookings?.flatMap((cb) =>
                  cb.courseParticipants.filter((cp) => !!cp.actorLicense).map((cp) => cp.actorId),
                )}
              />
            ),
          },
        ]
      : []),
    {
      icon: <PersonAddAlt1 />,
      text: Translate.get("AddStudent"),
      onClick: handleOpenBookingDialog,
    },
  ];

  async function handleDeleteCourse() {
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}courses/delete`,
      { courseId: course.courseId },
      "POST",
    );
    if (response && response.isSuccessful) {
      navigate("/dashboard/courses");
    }
    setShowDeleteCourseQuestion(false);
  }

  if (course && stats) {
    return (
      <>
        {isLoading && <Preloader />}
        {showBookingDialog && (
          <BookingFlowDialogContainer
            courseId={courseId}
            bookings={course.courseBookings}
            isELearningCourse={course.isELearningCourse}
            open={true}
            onClose={handleCloseBookingDialog}
          />
        )}
        <CourseGradesDialogBase courseId={courseId} open={showGradesDialog} onClose={handleCloseGradesDialog} />
        {showID06Dialog && <ID06CompetencesDialogBase courseId={courseId} onClose={handleCloseID06Dialog} />}
        {showSkillnationDialog && (
          <SkillnationDialogBase courseId={courseId} onClose={handleCloseSkillnationDialog} />
        )}
        {/*  {bigScreen ? ( */}
        <Big
          bigScreen={bigScreen}
          data={course}
          stats={stats}
          loadCourse={loadCourse}
          onDeleteCourse={() => setShowDeleteCourseQuestion(true)}
        />
        {/*  ) : (
          <Small
            data={course}
            stats={stats}
            loadCourse={loadCourse}
            onDeleteCourse={() => setShowDeleteCourseQuestion(true)}
          />
        )} */}
        <SpeedMenu content={speedDialActions} />
        <YesOrNoDialog
          open={showCourseFullyBookedDialog}
          title={Translate.get("CourseFullyBooked")}
          text={Translate.get("CourseFullyBookedUnpublishQuestion")}
          onNo={() => setShowCourseFullyBookedDialog(false)}
          onYes={async () => {
            await unpublishFromWeb();
            setShowCourseFullyBookedDialog(false);
          }}
          noText={Translate.get("No")}
          yesText={Translate.get("Yes")}
        />
        <YesOrNoDialog
          open={showDeleteCourseQuestion}
          title={Translate.get("RemoveCourse")}
          text={Translate.get("DoUWantToContinueWithDeleteCourse")}
          onNo={() => setShowDeleteCourseQuestion(false)}
          onYes={handleDeleteCourse}
          noText={Translate.get("Cancel")}
          yesText={Translate.get("Delete")}
        />
        <YesOrNoDialog
          open={showID06OpenQuestion}
          title={Translate.get("ID06Name")}
          text={Translate.get("OpenID06AfterDigitalLicenseQuestion")}
          onNo={() => setShowID06OpenQuestion(false)}
          onYes={handleOpenID06Dialog}
          noText={Translate.get("No")}
          yesText={Translate.get("Yes")}
        />
        <YesOrNoDialog
          open={showSkillnationOpenQuestion}
          title={Translate.get("SsgSkillnationName")}
          text={Translate.get("OpenSkillnationAfterDigitalLicenseQuestion")}
          onNo={() => setShowSkillnationOpenQuestion(false)}
          onYes={handleOpenSkillnationDialog}
          noText={Translate.get("No")}
          yesText={Translate.get("Yes")}
        />
      </>
    );
  }

  return (
    <>
      <Preloader invisible={true} />
    </>
  );
};

export default Bookings;
